export type Timezone = {
  tzName: string;
  gmtOffset?: number;
  gmtOffsetName?: string;
  abbreviation?: string;
  tzNameAlt?: string;
};

export type City = {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
};

export type State = {
  name?: string;
  state_code?: string;
  latitude?: string;
  longitude?: string;
  cities?: City[];
};

export type Translations = {
  [lang: string]: string;
};

export type CountryJSON = {
  id: number;
  name: string;
  iso3: string;
  iso2: string;
  numeric_code: string;
  phonecode: string;
  capital: string;
  currency: string;
  currency_name: string;
  currency_symbol: string;
  tld: string;
  native: string | null;
  region: string;
  region_id: number;
  subregion: string;
  subregion_id: number;
  nationality: string;
  timezones: Timezone[];
  translations: Translations | null;
  latitude: string;
  longitude: string;
  emoji: string;
  emojiU: string;
  states: State[];
};

export type CountriesData = CountryJSON[];

export type Option = {
  id: number | string;
  label: number | string;
};

export type ErrorObject = {
  [key: string]: string;
};

export type Binary = "YES" | "NO";

export type UserType =
  | "INDIVIDUAL"
  | "COMPANY"
  | "TRADER_CHANNEL_PARTNER"
  | "LAW_ENFORCEMENT";

export type Gender = "Male" | "Female" | "Other";
export const GENDERS: Gender[] = ["Male", "Female", "Other"];

export type Nation = {
  name: string;
  code: string;
};

export type User = {
  id?: number;
  username?: string;
  password?: string;
  userType?: UserType;
  oldPassword?: string;
  fullName?: string;
  loginFailCount?: string;
  roleId?: number;
  mobileVerified?: Binary;
  emailVerified?: Binary;
  isPaidService?: Binary;
  activeStatus?: Binary;
};

export type IndividualProfile = {
  userId?: number;
  firstName?: string;
  surname?: string;
  middleName?: string;
  nationalIdNumber?: string;
  cellPhone?: string;
  gender?: string;
  email?: string;
  mobileMoneyNumber?: string;
  nationality?: string;
  townCityId?: number;
  houseNumberStreet?: string;
  suburb?: string;
};

export type CompanyProfile = {
  userId?: number;
  companyName?: string;
  tradingName?: string;
  registrationNumber?: string;
  townCityId?: number;
  physicalAddress?: string;
  primaryContactFirstName?: string;
  primaryContactSurname?: string;
  primaryContactIdNumber?: string;
  primaryPassportNumber?: string;
  primaryContactRole?: string;
  primaryContactNumber?: string;
  primaryContactEmail?: string;
  billingContactFirstName?: string;
  billingContactSurname?: string;
  billingContactIdNumber?: string;
  billingContactPassportNumber?: string;
  billingContactEmail?: string;
  billingContactNumber?: string;
  primaryContactIdentityDocumentId?: string;
  certificateIncorpFileId?: string;
  cr14FileId?: string;
  companyType?: CompanyType;
};

export type CompanyType = "CORPORATE" | "TRADER_CHANNEL_PARTNER" | "INSURANCE";

export type LawEnforcementProfile = {
  id: number;
  firstName: string;
  surname: string;
  middleName: string;
  forceNumber: string;
  rankId: number;
  stationId: number;
  nationalIdNumber: string;
  cellPhone: string;
};

export type LawEnforcementRequest = {
  user: User;
  lawEnforcementProfile: LawEnforcementProfile;
  otpCode: number;
};

export type IndividualRequest = {
  user: User;
  profile: IndividualProfile;
};

export type CompanyRequest = {
  user: User;
  primaryContactIdentityDocument?: File;
  certificateIncorpFile?: File;
  cr14File?: File;
  profile: CompanyProfile;
};

export type Country = {
  label: string;
  value: string;
};

export type ServiceType = "FREE" | "PAID";

export type Town = {
  value: number;
  label: string;
};

export type Rank = {
  id: number;
  name: string;
  activeStatus: Binary;
};

export type Station = {
  id: number;
  name: string;
  activeStatus: Binary;
};

export enum RoleType {
  INDIVIDUAL = "INDIVIDUAL",
  CORPORATE = "CORPORATE",
  TRADER_CHANNEL_PARTNER = "TRADER_CHANNEL_PARTNER",
  INSURANCE = "INSURANCE",
  LAW_ENFORCEMENT = "LAW_ENFORCEMENT",
  LAW_ENFORCEMENT_ADMINISTRATOR = "LAW_ENFORCEMENT_ADMINISTRATOR",
  ADMINISTRATOR = "ADMINISTRATOR",
}

export enum BillingTemplateType {
  SEARCH = "SEARCH",
  TRACKING = "TRACKING",
}

export enum Currency {
  USD = "USD",
  ZIG = "ZIG",
}

export type RoleMenu = {
  id: number;
  menuName: string;
  menuId: number;
  roleId: number;
  activeStatus: Binary;
};

export type Role = {
  id: number;
  name: string;
  activeStatus: Binary;
  roleType: RoleType;
  roleMenus: RoleMenu[];
  menu: Menu[];
};

export type IndividualLookupData = {
  countrys: Country[];
  genders: string[];
  surburbs: any[];
  townCitys: Town[];
  serviceTypes: string[];
};

export type CompanyLookupData = {
  countrys: Country[];
  townCitys: Town[];
};

export type LawEnforcementLookupData = {
  rankList: Rank[];
  stationList: Station[];
  roleList: Role[];
};

export type OtpCodeRequest = {
  username: string;
  otpcode: number;
  ipAddress: string;
  country: string;
  isp: string;
};

export interface Menu {
  id: number;
  menuGroupId: number;
  name: string;
  menuFunction: string;
  menuGroupName: string;
  activeStatus: Binary;
}

export interface MenuGroup {
  id: number;
  activeStatus: Binary;
  name: string;
  menus: Menu[];
}

export type LoadingOverlayContextType = {
  setData: React.Dispatch<any>;
  loadingIncrement: () => void;
  loadingDecrement: () => void;
  data: any;
  loading: number;
  showToast: (toast: ToastProps) => void;
};

export enum ToastColor {
  Success = "success",
  Danger = "danger",
}

export enum ToastPlacement {
  TopLeft = "top-left",
  TopRight = "top-right",
  BottomLeft = "bottom-left",
  BottomRight = "bottom-right",
}

export type ToastProps = {
  id: number;
  color: ToastColor;
  closeButton?: boolean;
  title: string;
  timeout?: number;
  placement: ToastPlacement;
  timed?: boolean;
};

export type MenuView = {
  name: string;
  menuFunction: string;
};

export type MenuFetch = {
  name: string;
  menuViews: MenuView[];
};

export type IndividualView = {
  id?: number;
  firstName?: string;
  surname?: string;
  middleName?: string;
  nationalIdNumber?: string;
  cellPhone?: string;
  gender?: Gender;
  email?: string;
  mobileMoneyNumber?: string;
  nationality?: Country;
  townCityId?: number;
  houseNumberStreet?: string;
  suburb?: string;
};

export type CompanyView = {
  id?: number;
  companyName?: string;
  tradingName?: string;
  registrationNumber?: string;
  townCityId?: number;
  physicalAddress?: string;
  primaryContactFirstName?: string;
  primaryContactSurname?: string;
  primaryContactIdNumber?: string;
  primaryPassportNumber?: string;
  primaryContactRole?: string;
  primaryContactNumber?: string;
  primaryContactEmail?: string;
  billingContactFirstName?: string;
  billingContactSurname?: string;
  billingContactIdNumber?: string;
  billingContactPassportNumber?: string;
  billingContactEmail?: string;
  billingContactNumber?: string;
  primaryContactIdentityDocumentId?: string;
  certificateIncorpFileId?: string;
  cr14FileId?: string;
  companyType?: CompanyType;
};

export type Profile = {
  userId: number;
  username: string;
  fullName: string;
  isPaidService: Binary;
  activeStatus: Binary;
  profileType: string;
  townName: string;
  individualProfile?: IndividualView;
  companyProfile?: CompanyView;
};

export type Balance = {
  bundleName: string;
  credits: number;
  expiryDate: string;
  cost: number;
  used: number;
};

export type AccountStatus = {
  companyAccountActive: Binary;
  expiryDate: string;
  subscriptionName: string;
  cost: number;
};

// export type TopupDropdownList = {
//   mobileMoneyTypes: MobileMoneyType[];
// }
